import {
  BottomSheet,
  Pill,
  Text,
  Title,
  type UseModalState,
} from "@clipboard-health/ui-components";
import { DialogContent, Stack } from "@mui/material";
import { APP_V2_APP_EVENTS, logEvent } from "@src/appV2/lib/analytics";
import { openInBrowser } from "@src/appV2/NativeAdapters/browser.adapter";
import { useHistory, useParams } from "react-router-dom";

import { Button } from "../../components/Button";
import { DialogFooter } from "../../components/DialogFooter";
import { useInAppAdsFlag } from "../../ShiftDiscovery/ListView/useInAppAdsFeatureFlag";

interface InAppAdDetailsBottomSheetProps {
  modalState: UseModalState;
}

export function InAppAdDetailsBottomSheet(props: InAppAdDetailsBottomSheetProps) {
  const { modalState } = props;
  const { adId } = useParams<{ adId: string }>();
  const history = useHistory();

  // useCbhFlag returns `any` despite its type signature, causing all unsafe assignment errors below
  // As a result, we need to disable the no-unsafe-assignment rule for this line, and others until
  // we fix useCbhFLag. We use runtime validation to ensure we're safe.
  const adsFlag = useInAppAdsFlag();

  // Runtime validation to ensure type safety
  if (!adsFlag?.enabled || !adsFlag.ads || !Array.isArray(adsFlag.ads)) {
    return null;
  }

  const ad = adsFlag.ads.find((ad) => ad.attributes.appId === adId);

  // more runtime validation to ensure type safety
  if (!ad?.attributes) {
    return null;
  }

  const { title, longDescriptionHeadline, longDescriptionText, actions, appId } = ad.attributes;

  function handleActionClick(actionType: "primary" | "secondary" | "tertiary" | "quaternary") {
    const action = actions[actionType];
    if (!action?.link) {
      return;
    }

    logEvent(APP_V2_APP_EVENTS.AD_BUTTON_CLICKED, {
      adId: appId,
      actionType,
      linkType: action.linkType,
    });

    switch (action.linkType) {
      case "in-app": {
        history.push(action?.link);
        break;
      }

      case "external-in-app-browser": {
        void openInBrowser({ url: action?.link, presentationStyle: "fullscreen" });
        break;
      }

      case "external-out-of-app-browser": {
        void openInBrowser({ url: action?.link });
        break;
      }

      default: {
        break;
      }
    }
  }

  return (
    <BottomSheet
      modalState={modalState}
      footer={
        <DialogFooter
          orientation="vertical"
          onClose={() => {
            modalState.closeModal();
          }}
        />
      }
    >
      <DialogContent sx={{ paddingX: 10, paddingTop: 12, paddingBottom: 8 }}>
        <Stack spacing={6} alignItems="center" sx={{ paddingTop: 6 }}>
          <Pill
            size="medium"
            variant="filled"
            label="Clipboard Promo"
            iconType="star-filled"
            backgroundColor={(theme) => theme.palette.shift?.priority.background}
            color={(theme) => theme.palette.shift?.priority.text}
            iconColor={(theme) => theme.palette.shift?.priority.text}
          />

          <Title variant="h3" component="h3" sx={{ textAlign: "center", textWrap: "balance" }}>
            {title}
          </Title>

          <Text
            variant="h5"
            sx={{
              textAlign: "center",
              paddingX: 5,
            }}
          >
            {longDescriptionHeadline}
          </Text>

          <Text
            variant="body1"
            sx={{
              textAlign: "center",
              color: (theme) => theme.palette.text.secondary,
              paddingX: 5,
            }}
          >
            {longDescriptionText}
          </Text>

          <Stack spacing={4} sx={{ width: "100%", paddingTop: 4 }}>
            {actions.primary?.copy && (
              <Button
                fullWidth
                variant="contained"
                size="large"
                endIconType="chevron-right"
                onClick={() => {
                  handleActionClick("primary");
                }}
              >
                {actions.primary.copy}
              </Button>
            )}
            {actions.secondary?.copy && (
              <Button
                fullWidth
                variant="outlined"
                size="large"
                endIconType="exit"
                onClick={() => {
                  handleActionClick("secondary");
                }}
              >
                {actions.secondary.copy}
              </Button>
            )}
          </Stack>
        </Stack>
      </DialogContent>
    </BottomSheet>
  );
}
